import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  CUSTOMER_ADDRESSES_KEY,
  getCustomerAddresses,
} from "@/api/endpoints/address";
import { closeWebview } from "@/app/back";
import { ROUTES } from "@/app/routes";
import { ContractCard } from "@/components/ui/ContractCard";
import { ContractCardSkeleton } from "@/components/ui/ContractCardSkeleton";
import { ScreenHeader } from "@/components/ui/ScreenHeader";
import { ScreenMessage } from "@/components/ui/ScreenMessage";

import type { ChangeAddressLocationState } from "./ChangeAddressPage";
import { describeFailure } from "@/lib/failure";

interface ContractPageState {
  selected: string | null;
}

export default function DocumentAddressContractPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [selected, setSelected] = useState<string | null>(
    () => (location.state as ContractPageState | null)?.selected ?? null,
  );
  const [showReachLimit, setShowReachLimit] = useState(false);
  const addresses = useQuery({
    queryKey: CUSTOMER_ADDRESSES_KEY,
    queryFn: () => getCustomerAddresses(),
    staleTime: 60 * 60_000,
    gcTime: 60 * 60_000,
  });

  const failure =
    addresses.error === null || addresses.error === undefined
      ? null
      : describeFailure(addresses.error);

  const picked =
    addresses.data?.find((address) => address.accountNo === selected) ?? null;

  return (
    <>
      <ScreenHeader title="เปลี่ยนที่อยู่จัดส่งเอกสาร" />
      <main className="flex-1 bg-white px-[21px] pt-[28px] pb-[calc(120px+env(safe-area-inset-bottom))] tracking-[-0.011em]">
        {failure !== null ? (
          <ScreenMessage
            title={failure.title}
            description={failure.description}
            actionLabel={failure.action === "retry" ? "ลองใหม่" : "กลับ"}
            onAction={() => {
              if (failure.action === "retry") {
                void addresses.refetch();
                return;
              }
              closeWebview();
            }}
          />
        ) : (
          <>
            <h1 className="text-16 leading-6 font-bold">
              กรุณาเลือกสัญญาที่ต้องการเปลี่ยนที่อยู่
            </h1>

            <p className="mt-3 flex h-9.5 items-center gap-2.5 rounded-[10px] bg-warning px-3 py-6">
              <InfoIcon />
              <span className="text-14 font-light">
                สามารถเปลี่ยนที่อยู่จัดส่งเอกสารได้เฉพาะสัญญาที่ Active เท่านั้น
              </span>
            </p>

            <div className="mt-5 flex flex-col gap-4 px-1">
              {addresses.isPending
                ?
                  <>
                    <ContractCardSkeleton key={0} />
                    <ContractCardSkeleton key={1} />
                  </>
                : null}
              {addresses.data?.map((address) => (
                <ContractCard
                  key={address.accountNo}
                  address={address}
                  selected={address.accountNo === selected}
                  onSelect={() => {
                    setSelected((prev) =>
                      prev === address.accountNo ? null : address.accountNo,
                    );
                  }}
                />
              ))}
            </div>

            {addresses.data?.length === 0 ? (
              <ScreenMessage
                title="ไม่พบสัญญา"
                description="ไม่มีสัญญาที่เปลี่ยนที่อยู่จัดส่งเอกสารได้"
              />
            ) : null}
          </>
        )}
      </main>

      {failure === null ? (
        <div className="fixed inset-x-0 bottom-0 bg-white px-[30px] pb-4">
          <button
            type="button"
            disabled={picked === null}
            onClick={() => {
              if (picked === null) return;
              // Already used today's change on this contract: say so instead
              // of walking the customer through a form the API will reject.
              if (picked.canUpdate === false) {
                setShowReachLimit(true);
                return;
              }
              const here: ContractPageState = { selected };
              void navigate(ROUTES.settings.accountDocumentAddress, {
                replace: true,
                state: here,
              });
              const state: ChangeAddressLocationState = { current: picked };
              void navigate(ROUTES.settings.accountChangeAddress, { state });
            }}
            className="text-24 h-[51px] w-full rounded-[10px] bg-primary text-white disabled:bg-light-gray-5"
          >
            ยืนยัน
          </button>
        </div>
      ) : null}

      {showReachLimit ? (
        <ReachLimitModal
          onClose={() => {
            setShowReachLimit(false);
          }}
        />
      ) : null}
    </>
  );
}

function ReachLimitModal({ onClose }: { onClose: () => void }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="reach-limit-title"
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          onClose();
        }
      }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-6"
    >
      <div className="relative w-full max-w-[350px] rounded-[14px] bg-white px-5 pt-6 pb-6 text-center">
        <button
          type="button"
          autoFocus
          aria-label="ปิด"
          onClick={onClose}
          className="absolute top-3 right-3 flex h-8 w-8 items-center justify-center"
        >
          <CloseIcon />
        </button>

        <WarningTriangleIcon />

        <h2 id="reach-limit-title" className="text-18 mt-3 font-bold text-primary">
          พบข้อผิดพลาด
        </h2>
        <p className="text-16 mt-2 leading-6 font-bold">
          คุณสามารถเปลี่ยนที่อยู่จัดส่งเอกสารได้ 1 ครั้งต่อวัน
        </p>
      </div>
    </div>
  );
}

function WarningTriangleIcon() {
  return (
    <svg viewBox="0 0 48 48" className="mx-auto h-12 w-12 shrink-0" aria-hidden="true">
      <path d="M24 6 44 42H4Z" className="fill-error" strokeLinejoin="round" />
      <rect x="21.5" y="18" width="5" height="12" rx="1.5" fill="white" />
      <circle cx="24" cy="34" r="2.5" fill="white" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5 text-field-label" aria-hidden="true">
      <path
        d="M6 6 18 18M18 6 6 18"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg viewBox="0 0 16 16" className="h-4 w-4 shrink-0" aria-hidden="true">
      <circle
        cx="8"
        cy="8"
        r="7"
        fill="none"
        stroke="currentColor"
        strokeWidth="1"
      />
      <path
        d="M8 7v4.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <circle cx="8" cy="4.75" r="0.9" fill="currentColor" />
    </svg>
  );
}
